import { Badge, Group, Paper, Stack, Text } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { dashboardHinweiseSchema } from "@msc/shared";

import { api } from "../lib/api.js";

/**
 * „Was liegt an" für Bereichsleiter und Stellvertreter.
 *
 * **Zahlen allein reichen nicht.** Die erste Fassung zeigte nur Anzahlen und
 * verlinkte auf Übersichtsseiten — bei „210 Beauftragungen ohne Jahresziel"
 * hätte man danach 225 Kunden durchsehen müssen, um die gemeinten zu finden.
 * Deshalb nennt jeder Punkt jetzt Beispiele, und jedes Beispiel führt dorthin,
 * wo es sich erledigen lässt: das Jahresziel in die Kundenakte, der Bericht in
 * die Statusberichte.
 *
 * **Es erscheint nur, wenn etwas offen ist.** Ein Kasten mit dauerhaft „0" wird
 * nach zwei Wochen übersehen — und dann auch dann, wenn plötzlich eine Zahl
 * darin steht.
 */
export function OffenePunkteWidget() {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ["dashboard", "hinweise"],
    queryFn: async () => dashboardHinweiseSchema.parse(await api.get<unknown>("/dashboard/hinweise")),
    staleTime: 60_000,
    retry: false,
  });

  if (!data) return null;

  const punkte = [
    {
      schluessel: "projekteOhneBeauftragung" as const,
      hinweis: data.projekteOhneBeauftragung,
      ziel: (customerId: string | null) =>
        customerId ? { to: "/customers/$id", params: { id: customerId } } : null,
    },
    {
      // Ohne Service Lead taucht die Beauftragung in keiner Lead-Auswertung
      // auf. Erledigt wird es in der Kundenakte — bei „aus Jira" allerdings
      // im Asset, das sagt der Beispieltext dazu.
      schluessel: "beauftragungenOhneLead" as const,
      hinweis: data.beauftragungenOhneLead,
      ziel: (customerId: string | null) =>
        customerId ? { to: "/customers/$id", params: { id: customerId } } : null,
    },
    {
      schluessel: "beauftragungenOhneZiel" as const,
      hinweis: data.beauftragungenOhneZiel,
      ziel: (customerId: string | null) =>
        customerId ? { to: "/customers/$id", params: { id: customerId } } : null,
    },
    {
      schluessel: "statusberichteEntwurf" as const,
      hinweis: data.statusberichteEntwurf,
      ziel: () => ({ to: "/statusberichte", params: undefined }),
    },
    {
      // Kundenabteilungen haben keinen direkten Kundenbezug im Spiegel — der
      // Text nennt deshalb die Kundenfirma, damit man sie in der Kundenliste
      // wiederfindet.
      schluessel: "kundenabteilungenUnverknuepft" as const,
      hinweis: data.kundenabteilungenUnverknuepft,
      ziel: () => null,
    },
  ].filter((p) => p.hinweis.anzahl > 0);

  if (punkte.length === 0) return null;

  return (
    <Paper p="md" withBorder>
      <Text size="sm" fw={600}>
        {data.alleSegmente
          ? t("dashboard.hinweise.titelAlle")
          : t("dashboard.hinweise.titel")}
      </Text>
      <Text size="xs" c="dimmed" mb="sm">
        {data.alleSegmente ? t("dashboard.hinweise.untertitelAlle") : ""}
      </Text>

      <Stack gap="md">
        {punkte.map((p) => (
          <div key={p.schluessel}>
            <Group gap="sm" wrap="nowrap" mb={4}>
              {/*
                `flexShrink: 0` ist hier kein Feinschliff, sondern die
                Korrektheit: Die Zeile ist `wrap="nowrap"`, und ohne die Sperre
                drueckt der danebenstehende Text das Abzeichen zusammen, bis
                Mantine den Inhalt mit Auslassungspunkten kuerzt. Aus „18"
                wurde so „1.." — eine Zahl, die falsch ist statt nur haesslich.
                Einstellige Werte passten zufaellig, deshalb faellt es erst im
                Betrieb auf (2026-08-12).
              */}
              <Badge
                color="orange"
                variant="light"
                size="lg"
                style={{ flexShrink: 0 }}
              >
                {p.hinweis.anzahl}
              </Badge>
              <Text size="sm">
                {t(`dashboard.hinweise.${p.schluessel}`, {
                  count: p.hinweis.anzahl,
                })}
              </Text>
            </Group>
            <Stack gap={2} pl={48}>
              {p.hinweis.beispiele.map((b, i) => {
                const ziel = p.ziel(b.customerId);
                const inhalt = (
                  <Text size="xs" c="dimmed">
                    {b.text}
                  </Text>
                );
                return ziel ? (
                  <Link
                    key={`${b.text}-${i}`}
                    to={ziel.to}
                    params={ziel.params as never}
                    style={{ textDecoration: "none" }}
                  >
                    {inhalt}
                  </Link>
                ) : (
                  <div key={`${b.text}-${i}`}>{inhalt}</div>
                );
              })}
              {p.hinweis.anzahl > p.hinweis.beispiele.length ? (
                <Text size="xs" c="dimmed" fs="italic">
                  {t("dashboard.hinweise.weitere", {
                    count: p.hinweis.anzahl - p.hinweis.beispiele.length,
                  })}
                </Text>
              ) : null}
            </Stack>
          </div>
        ))}
      </Stack>
    </Paper>
  );
}
